/**
 * Client for the sandbox endpoints (backend/apps/sandbox): the /context index, what revives a
 * workspace, and the Files panel (its workspace, listing, download, upload, and sharing a file into
 * the conversation). A host's own changes go up through the sync layer, not here.
 */
import api, { UPLOAD_REQUEST_TIMEOUT_MS } from "@/services/api";
import type { MountEntry, MountSpec, SeedChange, WorkspaceSeed } from "../core/types";

const workspacePath = (workspaceId: string) =>
  `/agent/sandbox/workspaces/${encodeURIComponent(workspaceId)}`;

export type ContextMountEntry =
  | { path: string; kind: "text"; size: number; content: string }
  | {
      path: string;
      kind: "lazy";
      size: number | null;
      sha256: string | null;
      content_type: string;
      url: string;
    };

export interface ContextMountPayload {
  root: "/context";
  read_only: true;
  generated_at: string;
  entries: ContextMountEntry[];
}

export interface RestorePayload {
  archive: { version: number; url: string } | null;
  pending: {
    path: string;
    op: "put" | "delete";
    sha256: string | null;
    size: number | null;
    mtime: number | null;
    url: string | null;
  }[];
  exports: { path: string; url: string }[];
  /** sha256 of every stored file by workspace path. */
  index?: Record<string, string>;
  /** Files the host did not send (WS2): indexed, their bytes gone. */
  not_kept?: { path: string; size: number }[];
  epoch: number;
  seq: number;
}

/** How full a workspace is (it has no per-file limit, only this one). */
export interface WorkspaceUsage {
  size_bytes: number;
  max_bytes: number;
  free_bytes: number;
  /** 0 to 1, three decimals. */
  used_share: number;
}

/** One of the user's workspaces, as GET workspaces/ lists it. */
export interface SandboxWorkspaceEntry extends WorkspaceUsage {
  id: string;
  label: string;
  context_ref: string | null;
  thread_id: number | null;
  status: string;
  /** Every file in it, the Files panel's and the rest (work files, data exports). */
  files: number;
  archive_version: number;
  lease_live: boolean;
  last_used_at: string | null;
  /** The one the thread's calls run in (the server's current_workspace). Absent from an older
   * server and from POST's reply. */
  current?: boolean;
}

/** The label of the workspace a thread (or its context) gets unless the agent names another. */
export const DEFAULT_WORKSPACE_LABEL = "default";

/** A Files panel file: `path` starts with its folder, publish/ (the agent's) or uploads/ (the user's). */
export interface WorkspaceFile {
  path: string;
  size: number;
  class: "publish" | "uploads";
  /** false: the host did not send it (too big for its link, past a budget); no bytes to read. */
  kept?: boolean;
  sha256?: string;
  mtime?: number;
}

export type PanelFiles = { files: WorkspaceFile[] } & WorkspaceUsage;

/** An upload as stored: its path says the name it got (`name (2).ext` when the name was taken). */
export type UploadedFile = WorkspaceFile & WorkspaceUsage;

export async function getContextMount(workspaceId: string): Promise<ContextMountPayload> {
  const { data } = await api.get<ContextMountPayload>(
    `${workspacePath(workspaceId)}/context-mount/`
  );
  return data;
}

export async function getRestoreInfo(workspaceId: string): Promise<RestorePayload> {
  const { data } = await api.get<RestorePayload>(`${workspacePath(workspaceId)}/restore/`);
  return data;
}

export async function getPanelFiles(workspaceId: string): Promise<PanelFiles> {
  const { data } = await api.get<PanelFiles>(`${workspacePath(workspaceId)}/files/`);
  return data;
}

/** The thread's workspaces: its own, or its context's when the thread is bound to one. */
export async function getThreadWorkspaces(threadId: number): Promise<SandboxWorkspaceEntry[]> {
  const { data } = await api.get<{ workspaces: SandboxWorkspaceEntry[] }>(
    "/agent/sandbox/workspaces/",
    {
      params: { thread_id: threadId },
    }
  );
  return data.workspaces;
}

/** The thread's default workspace, made on first use. */
export async function createThreadWorkspace(threadId: number): Promise<SandboxWorkspaceEntry> {
  const { data } = await api.post<SandboxWorkspaceEntry>("/agent/sandbox/workspaces/", {
    thread_id: threadId,
  });
  return data;
}

/** Puts `file` in uploads/; a 413 says the workspace has no room for it. */
export async function uploadWorkspaceFile(workspaceId: string, file: File): Promise<UploadedFile> {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post<UploadedFile>(`${workspacePath(workspaceId)}/uploads/`, form, {
    timeout: UPLOAD_REQUEST_TIMEOUT_MS,
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

/** A Files panel file, shared into the conversation as a thread file (the next message can carry it). */
export interface PromotedFile {
  id: number;
  name: string;
  content_type: string;
  size: number;
}

export async function promoteWorkspaceFile(
  workspaceId: string,
  path: string,
  threadId: number
): Promise<PromotedFile> {
  const { data } = await api.post<PromotedFile>(`${workspacePath(workspaceId)}/promote/`, {
    path,
    thread_id: threadId,
  });
  return data;
}

/** A Files panel file's bytes, to save; never to render (it may be someone's HTML or SVG). */
export async function getWorkspaceFileBytes(
  workspaceId: string,
  path: string,
  options: { timeout?: number } = {}
): Promise<ArrayBuffer> {
  const encoded = path.split("/").map(encodeURIComponent).join("/");
  const { data } = await api.get<ArrayBuffer>(`${workspacePath(workspaceId)}/files/${encoded}`, {
    ...options,
    responseType: "arraybuffer",
  });
  return data;
}

/** One Files panel file's bytes (C9 `files/<path>`): an upload the live host pulls in (WS3). */
export async function getPanelFileBytes(workspaceId: string, path: string): Promise<Uint8Array> {
  const encoded = path.split("/").map(encodeURIComponent).join("/");
  const { data } = await api.get<ArrayBuffer>(`${workspacePath(workspaceId)}/files/${encoded}`, {
    responseType: "arraybuffer",
  });
  return new Uint8Array(data);
}

/** The context index as the executor mounts it (paths under /context, lazy entries by URL). */
export function toContextMount(payload: ContextMountPayload): MountSpec {
  const entries: MountEntry[] = payload.entries.map((entry) => {
    const path = `/context/${entry.path.replace(/^\/+/, "")}`;
    if (entry.kind === "text") return { path, size: entry.size, sha256: null, text: entry.content };
    return {
      path,
      size: entry.size,
      sha256: entry.sha256,
      contentType: entry.content_type,
      source: entry.url,
    };
  });
  return { root: "/context", readOnly: true, entries };
}

/** What revives /workspace: the archive, the pending changes in order (gzip blobs), the exports. */
export function toWorkspaceSeed(payload: RestorePayload): WorkspaceSeed {
  const pending: SeedChange[] = [];
  for (const change of payload.pending) {
    const path = `/workspace/${change.path}`;
    if (change.op === "delete") pending.push({ op: "delete", path });
    else if (change.url) {
      pending.push({
        op: "put",
        entry: {
          path,
          size: change.size,
          sha256: change.sha256,
          source: change.url,
          encoding: "gzip",
          mtime: change.mtime ? new Date(change.mtime).toISOString() : null,
        },
      });
    }
  }
  return {
    archive: payload.archive
      ? { source: payload.archive.url, version: payload.archive.version }
      : null,
    pending,
    exports: payload.exports.map((entry) => ({
      path: `/workspace/${entry.path}`,
      size: null,
      sha256: null,
      source: entry.url,
    })),
    index: payload.index ?? {},
    notKept: (payload.not_kept ?? []).map(({ path, size }) => ({
      path: `/workspace/${path}`,
      size,
    })),
  };
}
